import { ShopStore } from "./modules/shop/store";
import { withShop } from "./modules/shop/state";
import { deriveShopTheme } from "./modules/shop/checkout/theme";
import { z } from "zod";
import { canonicalJson, digest, hashBytes, hashText } from "./canonical";
import { PUBLISHED_ASSET_BROWSER_CACHE } from "./assetCache";
import { photoDimensions, photoSrcset, photoVariantKey, photoWidths } from "./photoSizes";
import { BuilderDatabase, mapAction } from "./postgres";
import { applyMigrations } from "./migrations";
import { BundleArtifactService } from "./bundles";
import { contentAssets } from "./content";
import { serveBundlePresentation } from "./presentation";
import { PostRevisionStore } from "./modules/blog/revisions";
import { BlogStore } from "./modules/blog/store";
import { withBlog } from "./modules/blog/state";
import { hasFeature, loadSiteFeatures } from "./features";
import { PostAssetStore } from "./modules/blog/photos";
import { shareCropKey } from "./shareCrop";
import { livePost, nextScheduledPost } from "./modules/blog/runtime";
const id = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/i);
/** Exported for a test that published pages may load the gtag script and reach Google. */
export const PUBLIC_BUNDLE_CSP = "default-src 'self' https: data: blob:; script-src 'self' 'unsafe-inline' https:; style-src 'self' 'unsafe-inline' https:; img-src 'self' data: blob: https:; font-src 'self' data: https:; object-src 'none'; base-uri 'self'; worker-src 'none'";
export const PublicationInput = z.discriminatedUnion("kind", [
    z.object({ actionId: id, kind: z.literal("publish"), baseDraftId: id, expectedServingEpoch: z.number().int().nonnegative() }).strict(),
    z.object({ actionId: id, kind: z.literal("rollback"), releaseId: id, expectedServingEpoch: z.number().int().nonnegative() }).strict(),
    z.object({ actionId: id, kind: z.literal("unpublish"), expectedServingEpoch: z.number().int().nonnegative() }).strict(),
]);
export class ReleaseService {
    db;
    objects;
    policy;
    options;
    bundles;
    constructor(db, objects, policy, options = {}) {
        this.db = db;
        this.objects = objects;
        this.policy = policy;
        this.options = options;
        this.bundles = new BundleArtifactService(objects);
    }
    async initialize() {
        await applyMigrations(this.db.pool);
    }
    async action(client, siteId, actionId) {
        const result = await client.query("SELECT * FROM builder_actions WHERE site_id=$1 AND id=$2", [siteId, actionId]);
        if (!result.rows[0])
            throw new Error("Action not found");
        return mapAction(result.rows[0]);
    }
    async prepare(siteId, actor, unknownInput) {
        const input = PublicationInput.parse(unknownInput);
        const inputDigest = await digest(input);
        const earlier = await this.db.getAction(siteId, input.actionId, actor);
        if (earlier) {
            if (earlier.inputDigest !== inputDigest || earlier.kind !== input.kind)
                throw new Error("Action ID reused with different input");
            return earlier;
        }
        const proposal = { input, manifestDigest: null, releaseId: null, renderDigest: null, rendererVersion: null, artifactDigest: null, presentationDigest: null };
        if (input.kind === "publish") {
            proposal.releaseId = `release-${crypto.randomUUID()}`;
            proposal.rendererVersion = "bundle-v1";
            const snapshot = await this.db.loadBundleSnapshot(siteId, input.baseDraftId);
            await this.bundles.load(siteId, snapshot.manifest.designDigest);
            proposal.manifestDigest = snapshot.manifest.digest;
            proposal.artifactDigest = snapshot.manifest.designDigest;
            proposal.presentationDigest = snapshot.manifest.presentationDigest;
            proposal.renderDigest = snapshot.manifest.presentationDigest;
        }
        return this.db.withSite(siteId, actor, async (client, site) => {
            const existing = await client.query("SELECT * FROM builder_actions WHERE site_id=$1 AND id=$2", [siteId, input.actionId]);
            if (existing.rows[0]) {
                const action = mapAction(existing.rows[0]);
                if (action.inputDigest !== inputDigest || action.kind !== input.kind)
                    throw new Error("Action ID reused with different input");
                return action;
            }
            if (site.servingEpoch !== input.expectedServingEpoch)
                throw new Error("Serving state changed; review again");
            if (input.kind === "publish") {
                if (site.draftId !== input.baseDraftId)
                    throw new Error("Draft changed; review again");
            }
            if (input.kind === "rollback") {
                const release = await client.query("SELECT * FROM builder_releases WHERE site_id=$1 AND id=$2 AND state='ready'", [siteId, input.releaseId]);
                if (!release.rows[0]?.inventory)
                    throw new Error("Ready retained release not found");
                proposal.manifestDigest = release.rows[0].manifest_digest;
                proposal.rendererVersion = release.rows[0].renderer_version;
                proposal.artifactDigest = release.rows[0].artifact_digest;
                proposal.presentationDigest = release.rows[0].presentation_digest;
            }
            const proposalDigest = await digest(proposal);
            await client.query(`INSERT INTO builder_actions(site_id,id,kind,input,input_digest,state,proposal,proposal_digest)
        VALUES($1,$2,$3,$4,$5,'awaiting-confirmation',$6,$7)`, [siteId, input.actionId, input.kind, input, inputDigest, proposal, proposalDigest]);
            return this.action(client, siteId, input.actionId);
        });
    }
    async confirm(siteId, actor, actionId, reviewedDigest) {
        const prepared = await this.db.getAction(siteId, actionId, actor);
        if (!prepared || prepared.proposalDigest !== reviewedDigest)
            throw new Error("Confirmation does not match prepared proposal");
        const preparedInput = PublicationInput.parse(prepared.input);
        if (preparedInput.kind === "rollback" && prepared.state === "awaiting-confirmation") {
            const row = await this.db.pool.query("SELECT * FROM builder_releases WHERE site_id=$1 AND id=$2 AND state='ready'", [siteId, preparedInput.releaseId]);
            const target = row.rows[0];
            if (!target?.inventory)
                throw new Error("Retained release unavailable");
            await this.verifyInventory(siteId, target.id, target.inventory);
        }
        return this.db.withSite(siteId, actor, async (client, site) => {
            const action = await this.action(client, siteId, actionId);
            if (action.proposalDigest !== reviewedDigest)
                throw new Error("Confirmation does not match prepared proposal");
            if (action.state === "succeeded" || (action.state === "queued" && action.confirmedBy))
                return action;
            if (action.state !== "awaiting-confirmation")
                throw new Error("Action cannot be confirmed");
            const input = PublicationInput.parse(action.input);
            if (site.servingEpoch !== input.expectedServingEpoch)
                throw new Error("Serving state changed; review again");
            const stored = await client.query("SELECT proposal FROM builder_actions WHERE site_id=$1 AND id=$2", [siteId, actionId]);
            const proposal = stored.rows[0].proposal;
            if (await digest(proposal) !== reviewedDigest)
                throw new Error("Prepared proposal integrity failure");
            if (input.kind !== "unpublish")
                await this.policy(client, site, actor);
            const intent = site.publishIntentSequence + 1;
            if (input.kind === "publish") {
                if (!proposal.releaseId || !proposal.renderDigest || !proposal.rendererVersion)
                    throw new Error("Publication output was not pinned");
                if (site.draftId !== input.baseDraftId)
                    throw new Error("Draft changed; review again");
                const manifest = (await this.db.loadBundleSnapshot(siteId, input.baseDraftId, client)).manifest;
                if (manifest.digest !== proposal.manifestDigest)
                    throw new Error("Approved manifest changed");
                await client.query(`INSERT INTO builder_releases(site_id,id,action_id,draft_id,manifest_digest,intent_sequence,state,renderer_version,artifact_digest,presentation_digest)
          VALUES($1,$2,$3,$4,$5,$6,'requested',$7,$8,$9)`, [siteId, proposal.releaseId, actionId, input.baseDraftId, proposal.manifestDigest, intent, proposal.rendererVersion, proposal.artifactDigest, proposal.presentationDigest]);
                await client.query("UPDATE builder_sites SET publish_intent_sequence=$2 WHERE id=$1", [siteId, intent]);
                await client.query("UPDATE builder_actions SET state='queued',confirmed_by=$3,confirmed_at=now(),updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId, actor.userId]);
            }
            else {
                let target = null;
                if (input.kind === "rollback") {
                    const release = await client.query("SELECT * FROM builder_releases WHERE site_id=$1 AND id=$2 AND state='ready'", [siteId, input.releaseId]);
                    if (!release.rows[0]?.inventory || release.rows[0].manifest_digest !== proposal.manifestDigest)
                        throw new Error("Retained release unavailable");
                    target = input.releaseId;
                }
                await client.query("UPDATE builder_sites SET active_release_id=$2,publish_intent_sequence=$3,serving_epoch=serving_epoch+1 WHERE id=$1", [siteId, target, intent]);
                await client.query("UPDATE builder_actions SET state='succeeded',confirmed_by=$3,confirmed_at=now(),result=$4,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId, actor.userId, { releaseId: target, servingEpoch: site.servingEpoch + 1 }]);
            }
            return this.action(client, siteId, actionId);
        });
    }
    async writeVerified(key, body, contentType, expectedHash) {
        const previous = await this.objects.get(key);
        if (previous) {
            if (await hashBytes(previous.body) !== expectedHash || previous.contentType !== contentType)
                throw new Error("Existing immutable artifact differs");
        }
        else
            await this.objects.put(key, body, contentType);
        const saved = await this.objects.get(key);
        if (!saved || await hashBytes(saved.body) !== expectedHash || saved.contentType !== contentType)
            throw new Error("Artifact verification failed");
    }
    async verifyInventory(siteId, releaseId, inventory) {
        const prefix = `releases/${siteId}/${releaseId}`;
        if (!Object.hasOwn(inventory.routes, "/") || !Object.hasOwn(inventory.files, "404.html"))
            throw new Error("Required routes or metadata are missing");
        for (const path of Object.values(inventory.routes))
            if (!Object.hasOwn(inventory.files, path))
                throw new Error("Route is absent from inventory");
        for (const [path, file] of Object.entries(inventory.files)) {
            const saved = await this.objects.get(`${prefix}/${path}`);
            if (!saved || await hashBytes(saved.body) !== file.sha256 || saved.contentType !== file.contentType)
                throw new Error("Release inventory is incomplete");
        }
        for (const asset of Object.values(inventory.assets)) {
            const saved = await this.objects.get(`${prefix}/${asset.path}`);
            if (!saved || await hashBytes(saved.body) !== asset.sha256 || saved.contentType !== asset.contentType)
                throw new Error("Release media is incomplete");
        }
        const saved = await this.objects.get(`${prefix}/inventory.json`);
        if (!saved || saved.contentType !== "application/json" || await hashBytes(saved.body) !== await digest(inventory))
            throw new Error("Durable inventory missing or corrupt");
    }
    async run(siteId, actor, actionId) {
        const start = await this.db.withSite(siteId, actor, async (client, site) => {
            const action = await this.action(client, siteId, actionId);
            if (action.state === "succeeded" || action.state === "conflict" || action.state === "cancelled")
                return { action, release: null };
            if (action.kind !== "publish" || !action.confirmedBy || !["queued", "running", "failed"].includes(action.state))
                throw new Error("Confirmed publish required");
            const row = await client.query("SELECT * FROM builder_releases WHERE site_id=$1 AND action_id=$2", [siteId, actionId]);
            const release = row.rows[0];
            if (!release)
                throw new Error("Pinned release is missing");
            const prepared = await client.query("SELECT proposal FROM builder_actions WHERE site_id=$1 AND id=$2", [siteId, actionId]);
            const proposal = prepared.rows[0].proposal;
            if (await digest(proposal) !== action.proposalDigest || proposal.releaseId !== release.id || proposal.manifestDigest !== release.manifest_digest
                || proposal.rendererVersion !== release.renderer_version || proposal.artifactDigest !== release.artifact_digest
                || proposal.presentationDigest !== release.presentation_digest)
                throw new Error("Prepared publication integrity failure");
            if (release.intent_sequence !== site.publishIntentSequence) {
                await client.query("UPDATE builder_actions SET state='conflict',error='Publication superseded',updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId]);
                return { action: await this.action(client, siteId, actionId), release: null };
            }
            await client.query("UPDATE builder_actions SET state='running',error=NULL,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId]);
            return { action, release, proposal };
        });
        if (!start.release)
            return start.action;
        const release = start.release;
        try {
            const prefix = `releases/${siteId}/${release.id}`;
            let inventory = release.inventory;
            const snapshot = await this.db.loadBundleSnapshot(siteId, release.draft_id);
            if (snapshot.manifest.digest !== release.manifest_digest || snapshot.manifest.designDigest !== release.artifact_digest
                || snapshot.manifest.presentationDigest !== release.presentation_digest || snapshot.manifest.presentationDigest !== start.proposal?.renderDigest) {
                throw new Error("Pinned bundle presentation integrity failure");
            }
            if (release.state !== "ready" || !inventory) {
                const materialized = await this.bundles.materializeRelease(siteId, snapshot.manifest.designDigest, release.id, { writeInventory: false });
                inventory = { ...materialized, assets: {} };
                if (hasFeature(await loadSiteFeatures(this.db.pool, siteId), "shop")) {
                    const shop = (await this.db.pool.query("SELECT settings FROM builder_shop WHERE site_id=$1", [siteId])).rows[0];
                    if (shop) {
                        try {
                            inventory.shopTheme = await deriveShopTheme(materialized, snapshot.content, shop.settings.base, async (file) => {
                                const stored = await this.objects.get(`${prefix}/${file}`);
                                if (!stored || await hashBytes(stored.body) !== materialized.files[file]?.sha256)
                                    throw new Error("Theme source is missing or corrupt");
                                return new TextDecoder().decode(stored.body);
                            });
                        }
                        catch (error) {
                            // Parser errors may include source excerpts, so log only the error class, never its message.
                            console.error("release.shop_theme_failed", { siteId, releaseId: release.id, errorName: error instanceof Error ? error.name : "UnknownError" });
                        }
                    }
                }
                for (const asset of contentAssets(snapshot.content)) {
                    const source = await this.objects.get(`originals/${siteId}/${asset.objectKey}`);
                    if (!source || await hashBytes(source.body) !== asset.sha256 || source.contentType !== asset.mimeType)
                        throw new Error("Original asset missing or corrupt");
                    const path = `media/${asset.assetId}/${asset.version}`;
                    await this.writeVerified(`${prefix}/${path}`, source.body, asset.mimeType, asset.sha256);
                    // The size lets pages offer smaller copies (`photoSizes.ts`); a photo without one is only ever served whole.
                    const dimensions = photoDimensions(source.body, asset.mimeType);
                    inventory.assets[`${asset.assetId}/${asset.version}`] = { path, sha256: asset.sha256, contentType: asset.mimeType, ...dimensions };
                }
                const serialized = canonicalJson(inventory);
                await this.writeVerified(`${prefix}/inventory.json`, serialized, "application/json", await hashText(serialized));
            }
            if (!inventory)
                throw new Error("Release inventory is missing");
            await this.verifyInventory(siteId, release.id, inventory);
            return await this.db.withSite(siteId, actor, async (client, site) => {
                const action = await this.action(client, siteId, actionId);
                if (action.state === "succeeded" || action.state === "cancelled")
                    return action;
                if (site.publishIntentSequence !== release.intent_sequence) {
                    await client.query("UPDATE builder_actions SET state='conflict',error='Publication superseded',updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId]);
                }
                else {
                    if (action.proposalDigest !== start.action.proposalDigest || !action.confirmedBy)
                        throw new Error("Confirmation changed");
                    await this.policy(client, site, actor);
                    await client.query("UPDATE builder_releases SET state='ready',inventory=$3 WHERE site_id=$1 AND id=$2", [siteId, release.id, inventory]);
                    await client.query("UPDATE builder_sites SET active_release_id=$2,serving_epoch=serving_epoch+1 WHERE id=$1", [siteId, release.id]);
                    await client.query("UPDATE builder_actions SET state='succeeded',result=$3,error=NULL,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId, { releaseId: release.id, draftId: release.draft_id, servingEpoch: site.servingEpoch + 1 }]);
                }
                return this.action(client, siteId, actionId);
            });
        }
        catch (error) {
            return this.db.withSite(siteId, actor, async (client) => {
                const current = await this.action(client, siteId, actionId);
                if (current.state === "succeeded" || current.state === "cancelled" || current.state === "conflict")
                    return current;
                await client.query("UPDATE builder_actions SET state='failed',error=$3,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, actionId, error instanceof Error ? error.message : "Release failed"]);
                return this.action(client, siteId, actionId);
            });
        }
    }
    async activeRelease(siteId) {
        const result = await this.db.pool.query(`SELECT r.*, a.updated_at AS published_at FROM builder_sites s JOIN builder_releases r
      ON r.site_id=s.id AND r.id=s.active_release_id JOIN builder_actions a ON a.site_id=r.site_id AND a.id=r.action_id
      WHERE s.id=$1 AND r.state='ready'`, [siteId]);
        return result.rows[0] ?? null;
    }
    /** Only the blog's live posts, as a snapshot: what photo and share-image authorization asks about. Posts live on their own, not in a release. */
    async liveBlogContent(siteId) {
        return withBlog({ schemaVersion: 1, siteId, id: "blog", recordsById: {}, collections: {} }, await this.db.loadBlog(siteId, { liveOnly: true }), "live");
    }
    /** Caller resolves and verifies the host before selecting siteId. Always read via uncached PG. */
    async serve(siteId, path) {
        const release = await this.activeRelease(siteId);
        if (!release?.inventory)
            return new Response("Not published", { status: 404, headers: { "Cache-Control": "no-store" } });
        if (!release.artifact_digest || !release.presentation_digest || !this.options.createHtmlRewriter)
            return new Response("Release unavailable", { status: 503, headers: { "Cache-Control": "no-store" } });
        // The blog goes live on its own, so its version is part of the key: publishing a post changes the key, and the old pages are never read again.
        // The version is read before the state, so a page cached under it is never older than it.
        // A site without the blog feature has its own key, so turning the feature off or on never serves a stale page.
        const blogs = new BlogStore(this.db.pool);
        const blogOn = hasFeature(await loadSiteFeatures(this.db.pool, siteId), "blog");
        const shops = new ShopStore(this.db.pool);
        const shopOn = hasFeature(await loadSiteFeatures(this.db.pool, siteId), "shop");
        const shopVersion = this.options.edgeCache && shopOn ? await shops.version(siteId) : 0;
        const blogVersion = this.options.edgeCache && blogOn ? await blogs.version(siteId) : 0;
        const cacheKey = this.options.edgeCache && !path.startsWith("/_stackje/") ? new Request(`https://builder-cache.stackje.invalid/${siteId}/${release.id}/b${blogOn ? blogVersion : "-off"}/s${shopOn ? shopVersion : "-off"}${path}`) : null;
        if (cacheKey) {
            const hit = await this.options.edgeCache.match(cacheKey).catch(() => undefined);
            if (hit) {
                const response = new Response(hit.body, hit);
                response.headers.set("Cache-Control", "no-store");
                return response;
            }
        }
        try {
            const snapshot = await this.db.loadBundleSnapshot(siteId, release.draft_id);
            if (snapshot.manifest.designDigest !== release.artifact_digest || snapshot.manifest.presentationDigest !== release.presentation_digest)
                throw new Error("Pinned bundle presentation changed");
            // The release pins the design and the Website's content; the blog's live posts, tags and settings are laid over it as they are now.
            const content = withShop(withBlog(snapshot.content, blogOn ? await blogs.load(siteId, { liveOnly: true }) : await blogs.hidden(siteId), "live"), shopOn ? await shops.load(siteId) : await shops.hidden(siteId), "live");
            const inventory = release.inventory;
            const mediaUrl = (asset) => `/_builder-assets/${siteId}/${release.id}/${asset.assetId}/${asset.version}`;
            // Smaller copies only when this worker can make them.
            const srcsets = new Map();
            if (this.options.resizePhoto) {
                for (const asset of contentAssets(snapshot.content)) {
                    const file = inventory.assets[`${asset.assetId}/${asset.version}`];
                    const srcset = file?.width && file.height ? photoSrcset(mediaUrl(asset), { width: file.width, height: file.height }) : undefined;
                    if (srcset)
                        srcsets.set(mediaUrl(asset), srcset);
                }
            }
            // The live posts' own photos (covers and bodies) get smaller copies too; their sizes are on the asset rows.
            if (this.options.resizePhoto) {
                for (const asset of await new PostAssetStore(this.db).listLive(siteId, content, new Date())) {
                    const srcset = asset.width && asset.height ? photoSrcset(mediaUrl(asset), { width: asset.width, height: asset.height }) : undefined;
                    if (srcset)
                        srcsets.set(mediaUrl(asset), srcset);
                }
            }
            const publishedAt = new Date(release.published_at).toISOString();
            const response = await serveBundlePresentation(this.bundles, siteId, release.artifact_digest, path, {
                content,
                createHtmlRewriter: this.options.createHtmlRewriter,
                mediaUrl,
                ...(srcsets.size ? { photoSrcset: (src) => srcsets.get(src) } : {}),
                contentSecurityPolicy: this.options.bundleContentSecurityPolicy ?? PUBLIC_BUNDLE_CSP,
                cacheControl: "no-store",
                noIndex: false,
                canonicalOrigin: snapshot.manifest.canonicalOrigin,
                lastModified: publishedAt,
                loadProductRevision: (hash) => shops.revision(siteId, hash),
                loadPostRevision: (hash) => new PostRevisionStore(this.db).load(siteId, hash),
                // Keyed by the cover's asset version and the crop, not the release, so it survives publishes and scraped link previews keep working.
                shareImageUrl: (cover, crop) => `/_builder-share/${siteId}/${cover.assetId}/${cover.version}/${shareCropKey(crop)}.jpg`,
            });
            if (cacheKey && response.status === 200 && response.headers.get("Content-Type")?.startsWith("text/html")) {
                const next = nextScheduledPost(content, new Date());
                const seconds = Math.max(1, Math.min(300, next ? Math.ceil((next.getTime() - Date.now()) / 1000) : 300));
                const stored = response.clone();
                stored.headers.set("Cache-Control", `public, max-age=${seconds}`);
                const work = this.options.edgeCache.put(cacheKey, stored).catch(() => undefined);
                if (this.options.waitUntil)
                    this.options.waitUntil(work);
                else
                    await work;
            }
            return response;
        }
        catch (error) {
            // Readers only see that the page is unavailable; the reason goes to the worker's logs.
            console.error("release.serve_failed", { siteId, releaseId: release.id, path, error: error instanceof Error ? `${error.name}: ${error.message}` : String(error) });
            return new Response("Release unavailable", { status: 503, headers: { "Cache-Control": "no-store" } });
        }
    }
    /**
     * A published photo, or with `width` its smaller WebP copy. A copy is made the first time it is
     * asked for and kept under `variants/`; when it can't be made, the original is served instead.
     */
    async serveAsset(siteId, releaseId, assetId, version, width) {
        const release = await this.activeRelease(siteId);
        let file = release?.id === releaseId ? release.inventory?.assets[`${assetId}/${version}`] : undefined;
        let postAsset = null;
        if (!file && release?.id === releaseId && /^[0-9]+$/.test(version)) {
            // Not a site photo: a post's own photo, served only while a live post of the pinned content uses it. One indexed query, no revision body.
            postAsset = await new PostAssetStore(this.db).findLive(siteId, assetId, Number(version), await this.liveBlogContent(siteId), new Date());
            postAsset ??= await new ShopStore(this.db.pool).findPhoto(siteId, assetId, Number(version));
            if (postAsset)
                file = { sha256: postAsset.sha256, contentType: postAsset.mimeType, ...(postAsset.width && postAsset.height ? { width: postAsset.width, height: postAsset.height } : {}) };
        }
        if (!file)
            return new Response("Not found", { status: 404, headers: { "Cache-Control": "no-store" } });
        const resize = this.options.resizePhoto;
        const copy = width && resize && photoWidths(file.width && file.height ? { width: file.width, height: file.height } : undefined).includes(width);
        if (width && !copy)
            return new Response("Not found", { status: 404, headers: { "Cache-Control": "no-store" } });
        const key = copy ? photoVariantKey(siteId, releaseId, assetId, version, width) : "";
        if (copy) {
            const kept = await this.objects.get(key);
            if (kept?.contentType === "image/webp")
                return publishedAsset(kept.body, "image/webp", await hashBytes(kept.body));
        }
        const stored = await this.objects.get(postAsset ? `originals/${siteId}/${postAsset.objectKey}` : `releases/${siteId}/${releaseId}/${file.path}`);
        if (!stored || await hashBytes(stored.body) !== file.sha256 || stored.contentType !== file.contentType)
            return new Response("Unavailable", { status: 503, headers: { "Cache-Control": "no-store" } });
        if (copy) {
            const made = await resize(stored.body, file.contentType, width).catch(() => null);
            if (made) {
                await this.objects.put(key, made, "image/webp");
                return publishedAsset(made, "image/webp", await hashBytes(made));
            }
        }
        return publishedAsset(stored.body, file.contentType, file.sha256);
    }
    /**
     * A post's share image (1200x630 JPEG), keyed by its cover's asset version and crop. Only a live
     * post's current cover and crop are served; the address doesn't name the release, so it is the
     * same across publishes and link previews already scraped keep working.
     */
    async serveShareImage(siteId, assetId, version, cropKey) {
        if (!this.options.sharePhoto || !/^[0-9]+$/.test(version) || !/^(auto|[0-9]+-[0-9]+-[0-9]+-[0-9]+)$/.test(cropKey))
            return new Response("Not found", { status: 404 });
        const release = await this.activeRelease(siteId);
        if (!release?.inventory)
            return new Response("Not found", { status: 404 });
        const content = await this.liveBlogContent(siteId);
        const now = new Date();
        const revisions = new PostRevisionStore(this.db);
        let crop;
        let found = false;
        for (const post of Object.values(content.recordsById)) {
            if (post.kind !== "post" || !livePost(post.fields, now) || post.fields.cover?.assetId !== assetId || String(post.fields.cover.version) !== version)
                continue;
            const revision = await revisions.load(siteId, post.fields.revision);
            if (revision && shareCropKey(revision.seo.imageCrop) === cropKey) {
                crop = revision.seo.imageCrop;
                found = true;
                break;
            }
        }
        let productAsset = null;
        if (!found) {
            const shops = new ShopStore(this.db.pool);
            const state = await shops.load(siteId);
            for (const product of state?.products ?? []) {
                const photo = product.details.photos[0];
                if (product.availability !== "available" || photo?.assetId !== assetId || String(photo.version) !== version)
                    continue;
                const revision = await shops.revision(siteId, product.details.revision);
                if (revision && shareCropKey(revision.seo.imageCrop) === cropKey) {
                    crop = revision.seo.imageCrop;
                    productAsset = await shops.asset(siteId, product.id, assetId, Number(version));
                    found = Boolean(productAsset);
                    break;
                }
            }
        }
        if (!found)
            return new Response("Not found", { status: 404 });
        const asset = productAsset ?? await new PostAssetStore(this.db).get(siteId, assetId, Number(version));
        if (!asset)
            return new Response("Not found", { status: 404 });
        const key = `variants/${siteId}/share/${assetId}/${version}/${cropKey}.jpg`;
        const kept = await this.objects.get(key);
        if (kept?.contentType === "image/jpeg")
            return publishedAsset(kept.body, "image/jpeg", await hashBytes(kept.body));
        const source = await this.objects.get(`originals/${siteId}/${asset.objectKey}`);
        if (!source || await hashBytes(source.body) !== asset.sha256)
            return new Response("Unavailable", { status: 503 });
        const made = await this.options.sharePhoto(source.body, asset.mimeType, crop);
        if (!made)
            return new Response("Not found", { status: 404 });
        await this.objects.put(key, made, "image/jpeg");
        return publishedAsset(made, "image/jpeg", await hashBytes(made));
    }
}
/** The address names the release, version and width, so these bytes never change there (see assetCache.ts). */
function publishedAsset(body, contentType, sha256) {
    return new Response(body, { headers: {
            "Content-Type": contentType, "Cache-Control": PUBLISHED_ASSET_BROWSER_CACHE, ETag: `"${sha256}"`,
            "X-Content-Type-Options": "nosniff", "Content-Security-Policy": "default-src 'none'; sandbox",
        } });
}
